import { useState } from 'react';
import { Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { RateView } from '@/types/product';
import { money, parseMoney, rateName } from './workbenchPricing';
import GroupedRates from './GroupedRates';
import { groupRates, rateGroup } from './rateGroups';

// Radix Select staat geen lege waarde toe; '' in de state betekent "alle groepen".
const ALL_GROUPS = '__all__';

interface Props {
  rates: RateView[];
  drafts: Record<string, { value: string; previousCents: number | null }>;
  busy: boolean;
  affected: string[];
  onChange: (id: string, value: string) => void;
  onChangeMany: (changes: Array<{ id: string; value: string }>) => void;
  onClose: () => void;
  width: number;
  onResize: (width: number) => void;
  onDelete: (rate: RateView) => void;
  canDelete: boolean;
}
export default function WorkbenchRatesPanel({
  rates,
  drafts,
  busy,
  affected,
  onChange,
  onChangeMany,
  onClose,
  width,
  onResize,
  onDelete,
  canDelete,
}: Props) {
  const [bulkOpen, setBulkOpen] = useState(false);
  const [percentage, setPercentage] = useState('');
  const [bulkGroup, setBulkGroup] = useState('');
  const pct = Number(percentage.replace(',', '.'));
  const validPct = percentage.trim() !== '' && Number.isFinite(pct) && pct > -100 && pct <= 1000;
  return (
    <aside
      aria-label="Tarieven wijzigen"
      className="relative flex flex-col self-start rounded border bg-card lg:sticky lg:top-6 lg:max-h-[calc(100dvh-7rem)]"
    >
      <div
        role="separator"
        aria-label="Breedte tarievenzijbalk"
        aria-orientation="vertical"
        aria-valuemin={304}
        aria-valuemax={640}
        aria-valuenow={width}
        tabIndex={0}
        title="Sleep om de tarievenzijbalk breder te maken"
        className="absolute -right-4 top-0 hidden h-full w-2 cursor-col-resize touch-none select-none items-center justify-center rounded hover:bg-info/20 focus-visible:bg-info/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info lg:flex"
        onPointerDown={(event) => {
          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId))
            onResize(
              event.clientX - event.currentTarget.parentElement!.getBoundingClientRect().left - 12
            );
        }}
        onPointerUp={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId))
            event.currentTarget.releasePointerCapture(event.pointerId);
        }}
        onKeyDown={(event) => {
          if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            onResize(
              event.key === 'Home'
                ? 304
                : event.key === 'End'
                  ? 640
                  : width + (event.key === 'ArrowLeft' ? -20 : 20)
            );
          }
        }}
      >
        <span className="h-10 w-0.5 bg-border" />
      </div>
      <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
        <h2 className="text-base font-semibold">Tarieven wijzigen</h2>
        <Button variant="ghost" size="icon" aria-label="Tarievenpaneel sluiten" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      <p className="shrink-0 px-4 pb-2 pt-4 text-xs leading-5 text-muted-foreground">
        Wijzig een uurtarief. De gekoppelde productprijzen rekenen direct mee.
      </p>
      <GroupedRates
        rates={rates}
        changedIds={new Set(Object.keys(drafts))}
        renderRate={(rate) => {
          const draft = drafts[rate.id];
          const value =
            draft?.value ??
            (rate.currentCents == null ? '' : String(rate.currentCents / 100).replace('.', ','));
          const invalid = parseMoney(value) === null;
          return (
            <div key={rate.id} className="py-3">
              <div className="mb-2 block text-sm font-semibold">
                <span className="flex items-center justify-between gap-2">
                  <label htmlFor={`workbench-rate-${rate.id}`}>{rateName(rate)}</label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    disabled={!canDelete}
                    aria-label={`Tarief ${rate.name} verwijderen`}
                    title={
                      canDelete
                        ? 'Tarief verwijderen en vervangen'
                        : 'Pas eerst je wijzigingen toe of verwerp ze'
                    }
                    onClick={(event) => {
                      event.preventDefault();
                      onDelete(rate);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">€</span>
                <Input
                  id={`workbench-rate-${rate.id}`}
                  inputMode="decimal"
                  disabled={busy}
                  value={value}
                  aria-invalid={invalid}
                  className="h-9 min-w-0 text-right tabular-nums aria-[invalid=true]:border-destructive"
                  onChange={(event) => onChange(rate.id, event.target.value)}
                />
                <span className="shrink-0 text-xs text-muted-foreground">/ uur</span>
              </div>
              {draft && !invalid && (
                <p className="mt-1 text-right text-xs text-muted-foreground">
                  Was {money(rate.currentCents)}
                </p>
              )}
              {invalid && (
                <p role="alert" className="mt-1 text-xs text-destructive">
                  Vul een bedrag vanaf € 0 in, met maximaal twee decimalen.
                </p>
              )}
              {rate.upcoming && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Gepland: {money(rate.upcoming.amountCents)} vanaf{' '}
                  {new Date(rate.upcoming.validFrom).toLocaleDateString('nl-NL')}
                </p>
              )}
            </div>
          );
        }}
      />
      {!rates.length && (
        <p className="p-4 text-sm text-muted-foreground">
          Nog geen tarieven. Voeg een tarief toe via Beheer → Tariefbeheer.
        </p>
      )}
      <div className="shrink-0 space-y-3 border-t p-4">
        <p className="text-xs leading-5 text-muted-foreground">
          {affected.length
            ? `${affected.length} ${affected.length === 1 ? 'product verandert' : 'producten veranderen'}: ${affected.slice(0, 3).join(', ')}${affected.length > 3 ? '…' : '.'}`
            : 'Wijzig een tarief om het effect te zien.'}{' '}
          Vaste prijzen blijven gelijk.
        </p>
        <button
          type="button"
          className="text-xs font-semibold text-[hsl(var(--info))] hover:underline"
          onClick={() => setBulkOpen(!bulkOpen)}
          aria-expanded={bulkOpen}
        >
          Tarieven verhogen…
        </button>
        {bulkOpen && (
          <div className="space-y-2">
            <label htmlFor="rate-increase-group" className="block text-xs">
              Voor welke groep?
            </label>
            <Select
              value={bulkGroup || ALL_GROUPS}
              onValueChange={(value) => setBulkGroup(value === ALL_GROUPS ? '' : value)}
            >
              <SelectTrigger id="rate-increase-group" className="h-8 bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_GROUPS}>Alle groepen</SelectItem>
                {groupRates(rates).map((group) => (
                  <SelectItem key={group.name} value={group.name}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label htmlFor="rate-percentage" className="block text-xs">
              Verhoging in procenten
            </label>
            <div className="flex items-center gap-2">
              <Input
                id="rate-percentage"
                inputMode="decimal"
                className="h-8"
                value={percentage}
                onChange={(event) => setPercentage(event.target.value)}
                placeholder="Bijv. 5"
              />
              <span>%</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={busy || !validPct}
              onClick={() => {
                const changes = rates
                  .filter((rate) => !bulkGroup || rateGroup(rate) === bulkGroup)
                  .flatMap((rate) => {
                    const cents = parseMoney(
                      drafts[rate.id]?.value ??
                        (rate.currentCents === null ? '' : String(rate.currentCents / 100))
                    );
                    return cents == null
                      ? []
                      : [
                          {
                            id: rate.id,
                            value: String(Math.round(cents * (1 + pct / 100)) / 100).replace(
                              '.',
                              ','
                            ),
                          },
                        ];
                  });
                onChangeMany(changes);
                setBulkOpen(false);
              }}
            >
              Voorbeeld berekenen
            </Button>
          </div>
        )}
      </div>
    </aside>
  );
}
